if (!customElements.get('stock-display')) {
    class StockDisplay extends HTMLElement {
        productAvailabilityCache = null;
        
        constructor() {
            super();
            this.currentStoreCookie = this.getCookie('currentStore');
            this.stockDisplayQueryId = this.dataset.stockDisplayQueryId;
            this.provinceSelectionElement = this.querySelector('#stock-display__province-selection');
            this.storeSelectionElement = this.querySelector('#stock-display__store-selection');
            this.storeMessageElement = this.querySelector('#stock-display__message');
            this.storeNameElement = this.querySelector('.stock-display__store-name');
            this.storeToolbarElement = this.querySelector('.stock-display__toolbar');
            this.storeResultElement = this.querySelector('.stock-display__row.result');
            this.deliveryStatusElement = this.querySelector('#stock-display__status-delivery .stock-display__placeholder');
            this.storeStatusElement = this.querySelector('#stock-display__status-store .stock-display__placeholder');
            this.collapsibleTriggerElements = this.querySelectorAll('.stock-display__collapsible-trigger');
            this.collapsibleElement = this.querySelector('#stock-display__collapsible');
            this.currentProductId = this.dataset.productId;
            this.currentVariantId = this.dataset.variantId;
            this.currentLocationId = this.currentStoreCookie?.dataId ?? '';
            this.inventoryByVariantId = {};
            this.init();
        }
        
        init() {
            const productId = this.currentProductId;
            const variantId = this.currentVariantId;
            const locationId = this.currentLocationId;
            this.loadProductAvailability(productId).then(() => {
                this.createSelections(variantId);
                this.setCurrentStore();
                this.setStockAvailability(variantId, locationId);
            });
            this.collapsibleEvent();
            this.onVariantChange();
        }
        
        async fetchProductAndLocationData(productId) {
            const url = `/apps/datajet/task/${this.stockDisplayQueryId}`;
            const payload = { productId };
            
            try {
                const response = await fetch(url, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload)
                });
                
                const responseJson = await response.json();
                if (response.ok && responseJson?.data?.product && responseJson?.data?.locations) {
                    return responseJson.data;
                } else {
                    console.error("Invalid response structure:", responseJson);
                    return {};
                }
            } catch (error) {
                console.error("Fetch Error:", error);
                return {};
            }
        }
        
        async getVariantAvailabilityByLocationId(variantId, locationId) {
            
            const stockAvailability = await this.loadProductAvailability(this.currentProductId);
            const variantKey = `gid://shopify/ProductVariant/${variantId}`;
            const locationKey = `gid://shopify/Location/${locationId}`;    
            const variantData = stockAvailability[variantKey];
            
            if (!variantData) {
                console.warn('No variant data found for key:', variantKey);
                return {
                    storeStockStatus: 'out-of-stock',
                    warehouseStockStatus: 'out-of-stock',
                };
            }
            
            const locationData = variantData.locations?.[locationKey];
            
            if (!locationData) {
                console.warn('No location data found for key:', locationKey);
                return {
                    storeStockStatus: 'out-of-stock',
                    warehouseStockStatus: 'out-of-stock',
                };
            }
            return locationData;
        }
        
        
        async loadProductAvailability(productId) {
            if (this.productAvailabilityCache) return this.productAvailabilityCache;
            
            const response = await this.fetchProductAndLocationData(productId);
            const { product, locations } = response;
            
            if (!product || !locations) {
                this.style.display = "none";
                return {};
            }
            
            product.variants.edges.forEach(({ node: variant }) => {
                const variantId = variant.id;
                const hideStockDisplay = variant.hideStockDisplay?.value ?? null;
                const locationsMap = {};
                let totalWarehouseAvailable = 0;
                let totalStoreAvailable = 0;
                
                variant.inventoryItem.inventoryLevels.edges.forEach(({ node: level }) => {
                    const loc = level.location;
                    const locationId = loc.id;
                    const availableQuantity = level.quantities.find(q => q.name === 'available')?.quantity ?? 0;
                    
                    if (!locationsMap[locationId]) {
                        locationsMap[locationId] = {
                            name: loc.name,
                            storeHidden: loc.storeHidden?.value === 'true',
                            storeEnable: loc.storeStockAvailability?.value === 'true',
                            warehouseEnable: loc.warehouseStockAvailability?.value === 'true',
                            inStockThreshold: parseInt(loc.inStockThreshold?.value) ?? 0,
                            outOfStockThreshold: parseInt(loc.outOfStockThreshold?.value) ?? 0,
                            storeAvailable: 0,
                            warehouseAvailable: 0,
                            totalAvailable: 0,
                            storeStockStatus: null,
                            warehouseStockStatus: null,
                        };
                    }
                    
                    const store = locationsMap[locationId];
                    store.totalAvailable += availableQuantity;
                    
                    if (store.storeEnable) {
                        store.storeAvailable += availableQuantity;
                        totalStoreAvailable += availableQuantity;
                    }
                    
                    if (store.warehouseEnable) {
                        store.warehouseAvailable += availableQuantity;
                        totalWarehouseAvailable += availableQuantity;
                    }
                    
                    store.storeStockStatus =
                        store.storeAvailable >= store.inStockThreshold ? 'in-stock' :
                            store.storeAvailable <= store.outOfStockThreshold ? 'out-of-stock' : 'call-store';
                    
                    store.warehouseStockStatus =
                        store.warehouseAvailable >= store.inStockThreshold ? 'in-stock' :
                            store.warehouseAvailable <= store.outOfStockThreshold ? 'out-of-stock' : 'low-in-stock';
                    
                    if (hideStockDisplay === 'true') {
                        store.storeStockStatus = 'call-store';
                    }
                });
                
                locations.edges.forEach(({ node }) => {
                    const locationId = node.id;
                    if (!locationsMap[locationId]) {
                        locationsMap[locationId] = {
                            name: node.name,
                            storeHidden: node.storeHidden?.value === 'true',
                            storeEnable: node.storeStockAvailability?.value === 'true',
                            warehouseEnable: node.warehouseStockAvailability?.value === 'true',
                            inStockThreshold: parseInt(node.inStockThreshold?.value) ?? 0,
                            outOfStockThreshold: parseInt(node.outOfStockThreshold?.value) ?? 0,
                            storeAvailable: 0,
                            warehouseAvailable: 0,
                            totalAvailable: 0,
                            storeStockStatus: 'out-of-stock',
                            warehouseStockStatus: null
                        };
                    }
                });
                
                this.inventoryByVariantId[variantId] = {
                    hideStockDisplay,
                    totalStoreAvailable,
                    totalWarehouseAvailable,
                    locations: locationsMap
                };
            });
            
            this.locationsList = locations.edges.map(edge => edge.node);
            this.productAvailabilityCache = this.inventoryByVariantId;
            return this.productAvailabilityCache;
        }
        
        async createSelections(variantId) {
            const locations = this.locationsList;
            if (!locations || locations.length === 0) {
                console.warn("No locations available.");
                return;
            }
            const currentLocationId = this.currentStoreCookie?.dataId ?? '';
            const currentLocationProvince = this.currentStoreCookie?.province ?? '';
            
            // Get store availability data first
            const storeChoicesList = await Promise.all(locations.map(async location => {
                const locationId = location.id.replace('gid://shopify/Location/', '');
                const stockData = await this.getVariantAvailabilityByLocationId(variantId, locationId);
                
                return {
                    value: location.name,
                    label: location.name,
                    selected: locationId === currentLocationId,
                    customProperties: {
                        id: locationId,
                        province: location.address.province,
                        storeHidden: stockData.storeHidden,
                        storeEnable: stockData.storeEnable,
                        stockStatus: stockData.storeStockStatus || 'out-of-stock',
                    },
                };
            }));
            
            // Filter out stores that are hidden or disabled
            const availableStores = storeChoicesList.filter(store => 
                store.customProperties.storeHidden !== true && 
                store.customProperties.storeEnable !== false
            );
            
            // Get unique provinces that have available stores
            const availableProvinces = [...new Set(availableStores.map(store => store.customProperties.province))];
            
            // Province selection setup - only show provinces with available stores
            const provinceSelectionElement = this.provinceSelectionElement;
            
            const provinceChoices = new Choices(provinceSelectionElement, {
                searchEnabled: false,
                searchChoices: false,
                itemSelectText: '',
                placeholder: "Select state",
                callbackOnCreateTemplates: function (template) {
                    return {
                        item: (classNames, data) => {
                            let prefix = 'State:';
                            let placeholderClass = '';
                            if (data.label === classNames.placeholderValue) {
                                prefix = '';
                                placeholderClass = 'choices__placeholder';
                            }
                            
                            return template(`
                                <div class="${classNames.classNames.item.join(' ')} ${placeholderClass} ${classNames.classNames.itemSelectable.join(' ')}" data-item data-id="${data.id}" data-value="${data.value}">
                                    ${prefix} ${data.label}
                                </div>
                            `);
                        }
                    };
                },
                choices: availableProvinces.map(province => ({
                    value: province,
                    label: province,
                    selected: province === currentLocationProvince,
                })),
            });
            
            // Store selection setup
            const storeChoicesListUnselected = storeChoicesList;
            
            const selectedProvince = provinceSelectionElement.value;
            const initialFilteredStores = selectedProvince
                ? availableStores.filter(choice => choice.customProperties.province === selectedProvince)
                : availableStores;
            
            const storeChoices = new Choices(this.storeSelectionElement, {
                searchEnabled: false,
                searchChoices: false,
                itemSelectText: '',
                callbackOnCreateTemplates: function (template) {
                    return {
                        choice: (classNames, data) => {
                            const storeHidden = data.customProperties?.storeHidden;
                            const storeEnable = data.customProperties?.storeEnable;
                            const stockStatus = data.customProperties?.stockStatus;
                            if (storeEnable === false) {
                                data.disabled = true;
                            }
                            const statusLabel = stockStatus === 'in-stock'
                                ? '<div class="stock-display__status in-stock"><div class="stock-display__dot"></div><div class="stock-display__label">In stock</div></div>'
                                : '<div class="stock-display__status out-of-stock"><div class="stock-display__dot"></div><div class="stock-display__label">Out of stock</div></div>';
                            return template(`
                                <div class="${(storeHidden === true) ? 'hidden' : ''} ${classNames.classNames.item.join(' ')} ${classNames.classNames.itemChoice.join(' ')} ${data.selected ? classNames.classNames.selectedState.join(' ') : '' } ${data.disabled ? classNames.classNames.itemDisabled.join(' ') : classNames.classNames.itemSelectable.join(' ') }" data-choice data-id="${data.id}" data-value="${data.value}" ${data.disabled ? 'data-choice-disabled aria-disabled="true"' : 'data-choice-selectable'}>
                                    <span class="store-name">${data.label}</span> ${statusLabel}
                                </div>
                            `);
                        }
                    };
                },
                placeholder: "Select store",
                choices: initialFilteredStores
            });
            
            this.provinceChoices = provinceChoices;
            this.storeChoices = storeChoices;
            this.storeChoicesList = storeChoicesListUnselected;
            this.availableStores = availableStores;
            
            provinceSelectionElement.addEventListener('change', (event) => {
                const selectedProvince = event.target.value;
                
                // Filter and update store choices - only show available stores for the selected province
                const filteredChoices = this.availableStores.filter(choice =>
                    choice.customProperties.province === selectedProvince
                );
                
                this.storeChoices.removeActiveItems();
                this.storeChoices.removeHighlightedItems();
                this.storeChoices.clearChoices(true, true);
                this.storeChoices.setChoices(filteredChoices, 'value', 'label', true);
            });
            
            this.storeSelectionElement.addEventListener('change', (event) => {
                if (this.deliveryStatusElement) this.deliveryStatusElement.textContent = "Loading...";
                if (this.storeStatusElement) this.storeStatusElement.textContent = "Loading...";
                
                const selectedStore = event.target.value;
                
                const filteredChoices = this.storeChoicesList.filter(choice =>
                    choice.value === selectedStore
                );
                
                const selectedDataId = filteredChoices[0].customProperties.id;
                const selectedProvince = filteredChoices[0].customProperties.province;
                const selectedStoreStockAvailability = filteredChoices[0].customProperties.stockStatus;
                
                this.storeChoices.setChoiceByValue(filteredChoices[0].value);
                this.provinceChoices.setChoiceByValue(selectedProvince);
                
                const storeData = {
                    name: selectedStore,
                    dataId: selectedDataId,
                    province: selectedProvince,
                    storeStockAvailability: selectedStoreStockAvailability
                };
                
                const collapsibleElement = this.collapsibleElement;
                this.toggleHide(collapsibleElement);
                this.setCookie('currentStore', storeData, 30);
                this.currentStoreCookie = this.getCookie('currentStore');
                this.setCurrentStore();
                this.setStockAvailability(this.currentVariantId, selectedDataId);
            });
        }
        
        setCurrentStore() {
            const resultElement = this.storeResultElement;
            if (this.currentStoreCookie) {
                if (this.storeNameElement) this.storeNameElement.innerHTML = this.currentStoreCookie.name;
                this.currentLocationId = this.currentStoreCookie.dataId;
                this.toggleShow(resultElement);
            } else {
                this.toggleHide(resultElement);
                if (this.storeStatusElement) this.storeStatusElement.innerHTML = '';
                if (this.deliveryStatusElement) this.deliveryStatusElement.innerHTML = '';
                this.currentLocationId = '';
            }
        }
        
        setStockStatusHtml(element, status) {
            if (element) {
                if (status === 'out-of-stock') {
                    element.innerHTML = `<div class="stock-display__status out-of-stock"><div class="stock-display__dot"></div><div class="stock-display__label">Out of stock</div></div>`;
                } else if (status === 'call-store') {
                    element.innerHTML = `<span>Call Store for Availability</span>`;
                } else if (status === 'check-store') {
                    element.innerHTML = ``;
                } else if (status === 'in-stock' || status === 'low-in-stock') {
                    element.innerHTML = `<div class="stock-display__status in-stock"><div class="stock-display__dot"></div><div class="stock-display__label">In stock</div></div>`;
                }
            }
        }
        
        async setStockAvailability(variantId, locationId) {
            const data = await this.getVariantAvailabilityByLocationId(variantId, locationId);
            if (data !== null) {
                this.setStockStatusHtml(this.storeStatusElement, data.storeStockStatus);
                this.setStockStatusHtml(this.deliveryStatusElement, data.warehouseStockStatus);
                
                if (data.storeStockStatus === 'out-of-stock') {
                    this.storeMessageElement.innerHTML = `Sorry, this product is unavailable at ${this.storeNameElement.textContent}.`;
                    this.storeToolbarElement.classList.remove('hide');
                } else {
                    this.storeMessageElement.innerHTML = '';
                    this.storeToolbarElement.classList.add('hide');
                }
            }
        }
        
        collapsibleEvent() {
            const collapsibleElement = this.collapsibleElement;
            this.collapsibleTriggerElements.forEach(trigger =>
                trigger.addEventListener('click', () => this.toggleShowHide(collapsibleElement))
            );
        }
        
        toggleShowHide(element) {
            if (!element) return;
            element.classList.toggle('hide');
            this.classList.toggle('collapsible-open');
            this.reCalculateAccordionHeight();
        }
        
        toggleHide(element) {
            if (!element) return;
            element.classList.add('hide');
        }
        
        toggleShow(element) {
            if (!element) return;
            element.classList.remove('hide');
        }
        
        reCalculateAccordionHeight() {
            const accordionContent = this.closest('.accordion-content');
            if (accordionContent) {
                accordionContent.style.maxHeight = `${accordionContent.scrollHeight + 15}px`;
            }
        }
        
        setCookie(name, value, days = 7) {
            const expires = new Date();
            expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
            const cookieValue = JSON.stringify(value);
            document.cookie = `${name}=${encodeURIComponent(cookieValue)};expires=${expires.toUTCString()};path=/`;
        }
        
        getCookie(name) {
            const nameEQ = name + "=";
            const ca = document.cookie.split(';');
            for (let i = 0; i < ca.length; i++) {
                let c = ca[i];
                while (c.charAt(0) === ' ') c = c.substring(1, c.length);
                if (c.indexOf(nameEQ) === 0) {
                    try {
                        return JSON.parse(decodeURIComponent(c.substring(nameEQ.length, c.length)));
                    } catch (e) {
                        console.error("Error parsing cookie value:", e);
                        return null;
                    }
                }
            }
            return null;
        }
        
        onVariantChange() {
            // Subscribe to variant change events using the pub/sub system
            this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, async (event) => {
                const variant = event.data?.variant?.id;
                
                if (variant) {
                    this.dataset.variantId = variant;
                    this.currentVariantId = variant;

                    this.setStockAvailability(variant, this.currentLocationId);
                    await this.updateStoreChoicesForVariant(variant);
                }
            });
        }
        
        disconnectedCallback() {
            // Clean up the variant change subscription
            if (this.variantChangeUnsubscriber) {
                this.variantChangeUnsubscriber();
            }
        }
        
        async updateStoreChoicesForVariant(variantId) {
            this.productAvailabilityCache = null;
            
            if (!this.storeChoices || !this.locationsList) {
                console.warn('Store choices or locations list not available');
                return;
            }
            
            const locations = this.locationsList;
            const currentLocationId = this.currentStoreCookie?.dataId ?? ''; 
            const updatedStoreChoicesList = await Promise.all(locations.map(async location => {
                const locationId = location.id.replace('gid://shopify/Location/', '');
                const stockData = await this.getVariantAvailabilityByLocationId(variantId, locationId);
                
                //console.log(`Store ${location.name} (${locationId}) stock status:`, stockData.storeStockStatus);
                
                return {
                    value: location.name,
                    label: location.name,
                    selected: locationId === currentLocationId,
                    customProperties: {
                        id: locationId,
                        province: location.address.province,
                        storeHidden: stockData.storeHidden,
                        storeEnable: stockData.storeEnable,
                        stockStatus: stockData.storeStockStatus || 'out-of-stock',
                    },
                };
            }));
            
            // Filter out stores that are hidden or disabled
            const updatedAvailableStores = updatedStoreChoicesList.filter(store => 
                store.customProperties.storeHidden !== true && 
                store.customProperties.storeEnable !== false
            );
            // Update the stored lists
            this.storeChoicesList = updatedStoreChoicesList;
            this.availableStores = updatedAvailableStores;
            
            // Update province choices if needed
            const updatedAvailableProvinces = [...new Set(updatedAvailableStores.map(store => store.customProperties.province))];
            if (this.provinceChoices) {
                this.provinceChoices.clearChoices(true, true);
                this.provinceChoices.setChoices(updatedAvailableProvinces.map(province => ({
                    value: province,
                    label: province,
                    selected: province === this.currentStoreCookie?.province,
                })), 'value', 'label', true);
            }
            
            // Update store choices for the currently selected province
            const selectedProvince = this.provinceSelectionElement?.value || this.currentStoreCookie?.province;
            const filteredChoices = selectedProvince
                ? updatedAvailableStores.filter(choice => choice.customProperties.province === selectedProvince)
                : updatedAvailableStores;
            
            if (this.storeChoices) {
                this.storeChoices.removeActiveItems();
                this.storeChoices.removeHighlightedItems();
                this.storeChoices.clearChoices(true, true);
                this.storeChoices.setChoices(filteredChoices, 'value', 'label', true);
            }
        }
    }
    
    customElements.define('stock-display', StockDisplay);
}